import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment';
import { Button, Modal } from '@arcflight/tf-component-library';
import TFInput from '../TFInput/TFInput';
import FileViewer from '../TFFileViewer/fileViewer';
import { MxAction } from '../../models/mxEvents';
import { AttachmentMutation } from '../../services/apiNew';
import useDraftAttachments, {
  buildAttachmentMutations,
  normalizeAttachmentForViewer,
} from '../../services/hooks/attachments/useDraftAttachments';
import { ButtonSize } from '../PaginatedDefectsTable/DefectTableHeader';
import { AircraftResource } from '../../models/aircraft';
import AuthDropdownMenu from '../AuthDropdownMenu/AuthDropdownMenu';

const CardRoot = styled.div`
  border: 1px solid rgba(36, 45, 65, 0.15);
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
  background-color: #fff;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`;

const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  margin-left: 12px;
`;

const CardMenuWrap = styled.div`
  flex-shrink: 0;
  width: 32px;
  display: flex;
  justify-content: flex-end;
`;

const CardEditDateRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const CardTitle = styled.strong`
  font-size: 14px;
  color: #242d41;
`;

const CardDate = styled.span`
  font-size: 12px;
  color: rgba(36, 45, 65, 0.6);
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: rgba(36, 45, 65, 0.6);
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.4px;
  color: ${({ theme }): string => theme.colours.graphite};
  margin-bottom: 4px;
  line-height: 1.33;
`;

const FormRow = styled.div`
  margin-bottom: 12px;
  &:last-of-type {
    margin-bottom: 16px;
  }
`;

const StyledTextarea = styled.textarea`
  border-radius: 4px;
  border-color: rgba(36, 45, 65, 0.1);
  width: 100%;
`;

const ReadOnlyDescriptionTextarea = styled(StyledTextarea)`
  margin: 0 0 8px;
  border-color: transparent;
  resize: none;
  cursor: default;
  color: rgba(36, 45, 65, 0.85);
  line-height: 1.4;
  overflow: hidden;
`;

const FormRowAttachmentsView = styled(FormRow)`
  margin-top: 24px;
`;

const EditFormActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const DeleteModalWrapper = styled.div`
  height: auto;
`;

const DeleteModalTitle = styled.div`
  font-weight: 500;
  color: #242d41;
  padding-bottom: 12px;
`;

const DeleteModalMessage = styled.div`
  line-height: 1.29;
  color: rgba(36, 45, 65, 0.7);
`;

const DeleteModalButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 28px;
`;

const DeleteModalButtonPrimary = styled.div`
  margin-right: 16px;
`;

type MxActionPersistPatch = {
  title?: string;
  description?: string;
  attachments?: AttachmentMutation[];
};

function getCreatedByName(action: MxAction): string {
  if (!action.created_by) return '';
  const { first_name, last_name } = action.created_by;
  return [first_name, last_name].filter(Boolean).join(' ').trim();
}

function getEditedByName(action: MxAction): string {
  if (!action.edited_by) return '';
  const { first_name, last_name } = action.edited_by;
  return [first_name, last_name].filter(Boolean).join(' ').trim();
}

function getLastEditedDisplay(iso: string | undefined, dateFormat: string): string | null {
  if (!iso) return null;
  const m = moment.utc(iso);
  return m.isValid() ? m.format(`${dateFormat} HH:mm`) : null;
}

export interface ActionCardProps {
  action: MxAction;
  dateFormat: string;
  aircraftId: string;
  resource: AircraftResource;
  actionNameSingular: string;
  canEdit: boolean;
  activeEditActionId: string | null;
  onActiveEditChange: (actionId: string | null) => void;
  onPersistUpdate: (actionId: string, patch: MxActionPersistPatch) => Promise<void>;
  onPersistDelete: (actionId: string) => Promise<void>;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  dateFormat,
  aircraftId,
  resource,
  actionNameSingular,
  canEdit,
  activeEditActionId,
  onActiveEditChange,
  onPersistUpdate,
  onPersistDelete,
}) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(action.title);
  const [draftDescription, setDraftDescription] = useState(action.description ?? '');
  const {
    draftAttachments,
    addDraftAttachment,
    handleDeleteClick: handleDraftAttachmentDelete,
    resetDraftAttachments,
  } = useDraftAttachments({ mode: 'edit', initialAttachments: action.attachments });
  const [isSaving, setIsSaving] = useState(false);
  const [editTitleError, setEditTitleError] = useState<string | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const readOnlyDescriptionRef = useRef<HTMLTextAreaElement>(null);

  const createdByName = getCreatedByName(action);
  const baseId = `MxItemActions-card-${action.id}`;

  useLayoutEffect(() => {
    if (isEditing) return;
    const el = readOnlyDescriptionRef.current;
    if (!el || !action.description) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [action.description, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    if (activeEditActionId !== null && activeEditActionId !== action.id) {
      setIsEditing(false);
      setDraftTitle(action.title);
      setDraftDescription(action.description ?? '');
      resetDraftAttachments(action.attachments);
      setEditTitleError(null);
    }
  }, [
    activeEditActionId,
    action.attachments,
    action.description,
    action.id,
    action.title,
    isEditing,
    resetDraftAttachments,
  ]);

  const beginEdit = () => {
    setDraftTitle(action.title);
    setDraftDescription(action.description ?? '');
    resetDraftAttachments(action.attachments);
    setEditTitleError(null);
    setIsEditing(true);
    onActiveEditChange(action.id);
  };

  const endEditSession = () => {
    setIsEditing(false);
    setDraftTitle(action.title);
    setDraftDescription(action.description ?? '');
    resetDraftAttachments(action.attachments);
    setEditTitleError(null);
    if (activeEditActionId === action.id) {
      onActiveEditChange(null);
    }
  };

  const handleCancel = () => {
    endEditSession();
  };

  const handleSave = async () => {
    const trimmedTitle = draftTitle.trim();
    if (!trimmedTitle) {
      setEditTitleError(
        formatMessage({ id: 'validation.actionTitle.required' }, { actionSingular: actionNameSingular }),
      );
      return;
    }
    setEditTitleError(null);
    const trimmedDesc = draftDescription.trim();
    const titleChanged = trimmedTitle !== action.title;
    const descriptionChanged = trimmedDesc !== (action.description ?? '');
    const attachmentOps = buildAttachmentMutations(draftAttachments);
    const attachmentChanged = attachmentOps.length > 0;
    if (!titleChanged && !descriptionChanged && !attachmentChanged) {
      endEditSession();
      return;
    }
    setIsSaving(true);
    try {
      await onPersistUpdate(action.id, {
        ...(titleChanged ? { title: trimmedTitle } : {}),
        ...(descriptionChanged ? { description: trimmedDesc || undefined } : {}),
        ...(attachmentChanged ? { attachments: attachmentOps } : {}),
      });
      endEditSession();
    } catch {
      // Parent shows toast; stay in edit mode
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await onPersistDelete(action.id);
      setDeleteModalVisible(false);
    } catch {
      // Parent shows toast
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <CardRoot data-testid={baseId}>
      {isEditing ? (
        <>
          <CardEditDateRow>
            <CardDate>
              {action.created_at ? moment(action.created_at).utc().format(`${dateFormat} HH:mm`) : ''}
            </CardDate>
          </CardEditDateRow>
          <FormRow>
            <TFInput
              id={`action-card-title-${action.id}`}
              value={draftTitle}
              onChange={(e) => {
                setDraftTitle(e.target.value);
                if (editTitleError) setEditTitleError(null);
              }}
              placeholder={formatMessage(
                { id: 'text.actionTitlePlaceholder' },
                { actionSingular: actionNameSingular },
              )}
              disabled={isSaving}
              status={editTitleError ? 'error' : undefined}
              statusMessage={editTitleError ?? ''}
            />
          </FormRow>
          <FormRow>
            <StyledTextarea
              id={`action-card-description-${action.id}`}
              name={`action-card-description-${action.id}`}
              data-testid={`${baseId}-description`}
              rows={5}
              value={draftDescription}
              onChange={(e): void => setDraftDescription(e.target.value)}
              placeholder={formatMessage(
                { id: 'text.actionDescriptionPlaceholder' },
                { actionSingular: actionNameSingular },
              )}
              disabled={isSaving}
            />
          </FormRow>
          <FormRow>
            <FieldLabel>{formatMessage({ id: 'text.attachments' })}</FieldLabel>
            <FileViewer
              attachments={draftAttachments}
              addAttachment={addDraftAttachment}
              handleDeleteClick={handleDraftAttachmentDelete}
              allowDelete={!isSaving}
              disableAddition={isSaving}
              fileInputId={`mx-action-edit-file-${action.id}`}
            />
          </FormRow>
          <EditFormActions>
            <Button size={ButtonSize.MEDIUM} onClick={handleCancel} disabled={isSaving}>
              {formatMessage({ id: 'text.cancel' })}
            </Button>
            <Button size={ButtonSize.MEDIUM} onClick={handleSave} disabled={isSaving}>
              {formatMessage({ id: 'text.actionSaveEdit' })}
            </Button>
          </EditFormActions>
        </>
      ) : (
        <>
          <CardHeader>
            <CardTitleRow>
              <CardTitle>{action.title}</CardTitle>
            </CardTitleRow>
            <CardHeaderRight>
              <CardDate>
                {action.created_at ? moment(action.created_at).utc().format(`${dateFormat} HH:mm`) : ''}
              </CardDate>
              {canEdit && (
                <CardMenuWrap data-testid={`${baseId}-actionsMenu`}>
                  <AuthDropdownMenu
                    options={{
                      read: false,
                      update: true,
                      delete: true,
                    }}
                    resource={resource}
                    aircraftId={aircraftId}
                    editCallback={beginEdit}
                    handleDelete={(): void => setDeleteModalVisible(true)}
                    forTable
                    noOffset
                  />
                </CardMenuWrap>
              )}
            </CardHeaderRight>
          </CardHeader>
        </>
      )}
      {!isEditing && action.description && (
        <ReadOnlyDescriptionTextarea
          ref={readOnlyDescriptionRef}
          readOnly
          value={action.description}
          rows={1}
          aria-label={formatMessage({ id: 'text.description' })}
        />
      )}
      {!isEditing && (action.attachments?.length ?? 0) > 0 && (
        <FormRowAttachmentsView>
          <FieldLabel>{formatMessage({ id: 'text.attachments' })}</FieldLabel>
          <FileViewer
            attachments={(action.attachments ?? []).map(normalizeAttachmentForViewer)}
            uploaderVisible={false}
            allowDelete={false}
          />
        </FormRowAttachmentsView>
      )}
      {createdByName ? (
        <CardMeta>{formatMessage({ id: 'text.loggedBy' }, { name: createdByName })}</CardMeta>
      ) : null}
      {(() => {
        const editedName = getEditedByName(action);
        const lastEditedDisplay = getLastEditedDisplay(action.edited_date, dateFormat);
        if (!editedName && !lastEditedDisplay) return null;
        return (
          <>
            {editedName ? (
              <CardMeta data-testid={`${baseId}-editedBy`}>
                {formatMessage({ id: 'text.actionLastEditedBy' }, { name: editedName })}
              </CardMeta>
            ) : null}
            {lastEditedDisplay ? (
              <CardMeta data-testid={`${baseId}-lastEdited`}>
                {formatMessage({ id: 'text.actionLastEdited' }, { datetime: lastEditedDisplay })}
              </CardMeta>
            ) : null}
          </>
        );
      })()}
      <Modal
        isOpen={deleteModalVisible}
        width={420}
        handleClose={(): void => !isDeleting && setDeleteModalVisible(false)}
      >
        <DeleteModalWrapper data-testid={`${baseId}-deleteModal`}>
          <DeleteModalTitle>{formatMessage({ id: 'title.deleteItem' })}</DeleteModalTitle>
          <DeleteModalMessage>{formatMessage({ id: 'text.deleteConfirm' })}</DeleteModalMessage>
          <DeleteModalButtonRow>
            <DeleteModalButtonPrimary>
              <Button
                size={ButtonSize.MEDIUM}
                padding="0 28px"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {formatMessage({ id: 'text.delete' })}
              </Button>
            </DeleteModalButtonPrimary>
            <Button
              size={ButtonSize.MEDIUM}
              padding="0 28px"
              primary={false}
              onClick={(): void => setDeleteModalVisible(false)}
              disabled={isDeleting}
            >
              {formatMessage({ id: 'text.cancel' })}
            </Button>
          </DeleteModalButtonRow>
        </DeleteModalWrapper>
      </Modal>
    </CardRoot>
  );
};

export default ActionCard;
